/**
 * `telegraphed-caster`: hold medium range, commit to an announced area, and
 * keep a lesser attack for the gaps (PRD #793).
 *
 * A caster is the role that asks the player to move. It stands further out than
 * an archer, and its main answer is an area it announces before anything
 * happens in it - which means the cast is a commitment on the caster's side too:
 * it is rooted for the wind-up, and by the time the area lands the player has
 * had the whole lead to leave it.
 *
 * Two rules keep it from being either harmless or oppressive.
 *
 * **Line of sight is required at cast start.** An area announced through a wall
 * is an area the player never saw coming, so a caster that cannot see its target
 * closes until it can rather than casting blind. Nothing re-checks sight
 * afterwards, because the cast is frozen and the announcement is already made:
 * ducking behind a pillar after the telegraph does not cancel it, walking out of
 * the footprint does.
 *
 * **A lesser attack fills the gap.** Its area ability is on a long cooldown, and
 * a caster with nothing to do between casts is a caster the player can ignore
 * for ten seconds, so it falls back to a modest projectile.
 *
 * **Rng draws: one per committed reposition**, inherited from the shared band
 * policy, and none otherwise.
 */

import type { AbilityDefinition } from '../abilityCatalog';
import {
  clearCommitment,
  continueCommitment,
  holdBand,
  type RepositionBand,
} from './bandHolding';
import { currentTarget, rememberTarget } from './perception';
import type { Brain, BrainDecision, BrainPerception, PerceivedAbility } from './types';

/** The caster's band, in tiles: further out than the archer's, and slower to give ground. */
export const TELEGRAPHED_CASTER_BAND: RepositionBand = {
  pressureTiles: 4,
  farTiles: 8,
  repositionTiles: 3,
  commitTicks: 14,
};

/**
 * What to cast: the announced area when it is ready, the lesser attack
 * otherwise. Both are chosen by mechanical property rather than by id, so an
 * archetype can swap either without touching this module.
 */
function chooseCast(
  perception: BrainPerception,
  distanceTiles: number,
): PerceivedAbility | undefined {
  const inReach = (ability: PerceivedAbility) =>
    ability.ready && ability.rangeTiles >= distanceTiles;
  return (
    perception.abilities.find(
      (ability) => inReach(ability) && ability.counterplay === 'telegraphed',
    ) ??
    perception.abilities.find(
      (ability) => inReach(ability) && ability.shapeKind === 'projectile',
    )
  );
}

export const telegraphedCaster: Brain = {
  id: 'telegraphed-caster',

  loadoutProblems(abilities: readonly AbilityDefinition[]): string[] {
    const problems: string[] = [];
    if (!abilities.some((ability) => ability.counterplay === 'telegraphed')) {
      problems.push('telegraphed-caster needs a telegraphed ability to commit to');
    }
    if (!abilities.some((ability) => ability.shape.kind === 'projectile')) {
      problems.push(
        'telegraphed-caster needs a lesser projectile so it is not harmless between casts',
      );
    }
    return problems;
  },

  decide(perception, memory, rng): BrainDecision {
    const remembered = rememberTarget(memory, perception);
    const intent = {
      target: remembered.target,
      desiredRangeTiles: TELEGRAPHED_CASTER_BAND.farTiles,
    };
    if (perception.casting) {
      // Rooted for the wind-up. A caster that walked while committing would be
      // announcing an area from a place it is no longer standing.
      return {
        memory: remembered,
        intent: { ...intent, move: { kind: 'hold' }, cast: null },
      };
    }
    if (remembered.target === null || !remembered.lastKnown) {
      return {
        memory: clearCommitment(remembered),
        intent: { ...intent, move: { kind: 'idle' }, cast: null },
      };
    }
    const committed = continueCommitment(remembered);
    if (committed) {
      return {
        memory: committed.memory,
        intent: {
          ...intent,
          move: { kind: 'retreat', point: committed.point },
          cast: null,
        },
      };
    }
    const target = currentTarget(perception, remembered);
    if (!target?.visible) {
      return {
        memory: clearCommitment(remembered),
        intent: {
          ...intent,
          move: { kind: 'approach', point: { ...remembered.lastKnown } },
          cast: null,
        },
      };
    }
    const band = holdBand(
      perception.self.position,
      target,
      TELEGRAPHED_CASTER_BAND,
      remembered,
      rng,
    );
    if (band.kind === 'retreat') {
      return {
        memory: band.memory,
        intent: { ...intent, move: { kind: 'retreat', point: band.point }, cast: null },
      };
    }
    if (band.kind === 'approach') {
      return {
        memory: clearCommitment(remembered),
        intent: { ...intent, move: { kind: 'approach', point: band.point }, cast: null },
      };
    }
    const chosen = chooseCast(perception, target.distanceTiles);
    return {
      memory: clearCommitment(remembered),
      intent: {
        ...intent,
        move: { kind: 'hold' },
        cast: chosen ? { abilityId: chosen.id, aim: { ...target.position } } : null,
      },
    };
  },
};
